import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { applyShopTheme } from '@/lib/shopApi';
import { Storefront, Lock, CheckCircle, Warning, Buildings } from '@phosphor-icons/react';

export default function ShopSetupPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [leverancier, setLeverancier] = useState(null);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/shop/leverancier`, { params: { token } })
      .then((r) => { setLeverancier(r.data); applyShopTheme(r.data?.primary_kleur); })
      .catch(() => setLeverancier(null));
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (pw.length < 6) return setErr('Wachtwoord moet minstens 6 tekens lang zijn');
    if (pw !== pw2) return setErr('Wachtwoorden komen niet overeen');
    setBusy(true);
    try {
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/shop/setup/${token}`, { password: pw });
      localStorage.setItem('shop_token', r.data.token);
      navigate('/shop/bestellen');
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Setup mislukt');
    } finally {
      setBusy(false);
    }
  };

  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const logoUrl = leverancier?.logo ? `${apiBase}${leverancier.logo}` : null;

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(to bottom right, var(--shop-primary), var(--shop-primary-dark))' }}>
      <form onSubmit={onSubmit} className="bg-white shadow-2xl max-w-md w-full p-8" data-testid="shop-setup-form">
        {leverancier ? (
          <div className="text-center mb-6 pb-5" style={{ borderBottom: '2px solid color-mix(in srgb, var(--shop-primary) 10%, transparent)' }} data-testid="shop-setup-leverancier">
            {logoUrl ? (
              <img src={logoUrl} alt={leverancier.naam} className="h-14 mx-auto mb-2 object-contain" />
            ) : (
              <Buildings size={36} weight="bold" className="shop-text-primary mx-auto mb-1" />
            )}
            <div className="text-xs uppercase font-bold text-gray-400 tracking-widest">Welkom bij de online shop van</div>
            <div className="text-2xl font-black shop-text-primary leading-tight">{leverancier.naam}</div>
            {leverancier.klant_naam && (
              <div className="text-xs text-gray-500 mt-1.5">Account voor <strong>{leverancier.klant_naam}</strong></div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 mb-6">
            <Storefront size={28} weight="bold" className="shop-text-primary" />
            <div>
              <div className="text-xl font-black shop-text-primary">Wachtwoord aanmaken</div>
              <div className="text-xs text-gray-500">Eenmalige setup voor uw shop-account</div>
            </div>
          </div>
        )}
        <div className="text-center mb-4 text-sm font-semibold text-gray-700">
          Kies een wachtwoord om uw shop-account te activeren
        </div>
        {err && (
          <div className="mb-4 p-2 bg-red-50 border border-red-200 text-xs text-red-700 flex items-start gap-1">
            <Warning size={14} className="mt-0.5 shrink-0" /> {err}
          </div>
        )}
        <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Nieuw wachtwoord</label>
        <div className="relative mb-4">
          <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="password" className="w-full border-2 border-gray-200 shop-ring-primary outline-none pl-9 pr-3 py-2.5 text-sm" value={pw} onChange={(e) => setPw(e.target.value)} minLength={6} required data-testid="shop-setup-pw1" />
        </div>
        <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Bevestig wachtwoord</label>
        <div className="relative mb-6">
          <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="password" className="w-full border-2 border-gray-200 shop-ring-primary outline-none pl-9 pr-3 py-2.5 text-sm" value={pw2} onChange={(e) => setPw2(e.target.value)} minLength={6} required data-testid="shop-setup-pw2" />
        </div>
        <button type="submit" disabled={busy} className="w-full shop-bg-primary text-white font-bold py-3 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="shop-setup-submit">
          <CheckCircle size={16} weight="bold" /> {busy ? 'Opslaan…' : 'Aanmaken & aanmelden'}
        </button>
      </form>
    </div>
  );
}
